import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { getFilterValues, getAppliedFilter } from '../sbSharedRefineMenuHelpers.ts';
import { type FilterValue } from '../SbSharedRefineMenuMultiSelect/SbSharedRefineMenuMultiSelect.tsx';

// components
import { SbSharedRefineMenuRadioSelect } from './SbSharedRefineMenuRadioSelect.jsx';
import { SbSharedRefineMenuTypeaheadContainer } from '../SbSharedRefineMenuTypeahead/SbSharedRefineMenuTypeaheadContainer.tsx';

import { type SbSharedRefineMenuRadioSelectSearch_viewer$key } from './__generated__/SbSharedRefineMenuRadioSelectSearch_viewer.graphql.ts';
import { type SbSharedRefineMenuRadioSelectSearch_itemSearch$key } from './__generated__/SbSharedRefineMenuRadioSelectSearch_itemSearch.graphql.ts';

const viewerFragment = graphql`
    fragment SbSharedRefineMenuRadioSelectSearch_viewer on Viewer {
        ...SbSharedRefineMenuTypeaheadContainer_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuRadioSelectSearch_itemSearch on ItemSearchQueryConnection {
        displayUriRef
        ...SbSharedRefineMenuTypeaheadContainer_itemSearch
        appliedFilters {
            name
            localizedFilterName
            canBeDismissed
            values {
                urlLabel
                linkReference
                count
                displayName
            }
        }
        searchResultData {
            vectorResultCount
        }
    }
`;

type Props = {
    viewer: SbSharedRefineMenuRadioSelectSearch_viewer$key;
    itemSearch: SbSharedRefineMenuRadioSelectSearch_itemSearch$key;
    values: FilterValue[];
    filterName: string;
    totalCount?: number;
    showSearchBar?: boolean;
    trackingOptions?: Record<string, unknown>;
};

export const SbSharedRefineMenuRadioSelectSearch: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    values,
    filterName,
    totalCount = 0,
    showSearchBar,
    trackingOptions,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { appliedFilters, displayUriRef } = itemSearch;
    const hasVectorSearchResults = !!itemSearch?.searchResultData?.vectorResultCount;
    const appliedFilter = getAppliedFilter(appliedFilters, filterName);
    const appliedFilterValues = getFilterValues(appliedFilters, filterName);
    const canBeDismissed = appliedFilter ? appliedFilter.canBeDismissed : true;

    return (
        <SbSharedRefineMenuTypeaheadContainer
            viewer={viewer}
            filterName={filterName}
            itemSearch={itemSearch}
            availableFilterValues={values}
            showSearchBar={showSearchBar}
            trackingOptions={trackingOptions}
        >
            {({ valuesToShow }) => (
                <SbSharedRefineMenuRadioSelect
                    filterName={filterName}
                    totalCount={totalCount}
                    valuesToShow={valuesToShow}
                    displayUriRef={displayUriRef}
                    canBeDismissed={canBeDismissed}
                    appliedFilterValues={appliedFilterValues}
                    trackingOptions={trackingOptions}
                    hasVectorSearchResults={hasVectorSearchResults}
                />
            )}
        </SbSharedRefineMenuTypeaheadContainer>
    );
};
